import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { AlertTriangle, DollarSign, Users, CalendarOff, Clock, Gem, TrendingUp } from 'lucide-react';

const CATEGORY_META = {
  'No Show': { icon: CalendarOff, color: 'text-red-500', bg: 'bg-red-50' },
  'Unscheduled Treatment': { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-50' },
  'Inactive Patient': { icon: Users, color: 'text-slate-500', bg: 'bg-slate-100' },
  'Cosmetic / Veneer Drop-off': { icon: Gem, color: 'text-violet-500', bg: 'bg-violet-50' },
  'High-Value Unsold': { icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-50' },
  'Long Overdue Follow-up': { icon: TrendingUp, color: 'text-blue-500', bg: 'bg-blue-50' },
};

export default function RevenueLeak() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const { data: d } = await api().get('/api/import/leaks');
        setData(d);
      } catch (e) { console.error(e); }
      setLoading(false);
    }
    load();
  }, [api]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="w-6 h-6 border-2 border-slate-900 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const hasData = data && (data.total_leaks > 0 || data.leads_auto_added > 0);

  return (
    <div data-testid="revenue-leak-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Revenue Leak Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">AI-detected opportunities from imported patient data</p>
        </div>
        <Link to="/import-data" data-testid="import-more-btn" className="text-sm font-medium bg-slate-900 text-white px-4 py-2 rounded-lg hover:bg-slate-800 transition-all">
          Import More Data
        </Link>
      </div>

      {!hasData ? (
        <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
          <AlertTriangle size={40} className="mx-auto text-slate-300 mb-4" />
          <h2 className="text-lg font-semibold text-slate-900">No revenue leaks detected yet</h2>
          <p className="text-sm text-slate-500 mt-2">Import your patient data to scan for missed revenue opportunities.</p>
          <Link to="/import-data" className="mt-4 inline-block bg-slate-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all">
            Import Patient Data
          </Link>
        </div>
      ) : (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Potential Revenue Leak</p>
              <p className="text-3xl font-bold text-red-600 mt-2">${(data.potential_revenue || 0).toLocaleString()}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Total Leaks Found</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">{data.total_leaks}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">Leads Auto-Added</p>
              <p className="text-3xl font-bold text-emerald-600 mt-2">{data.leads_auto_added}</p>
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-6">
              <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">High-Value Leads</p>
              <p className="text-3xl font-bold text-slate-900 mt-2">{data.high_value_leads}</p>
            </div>
            {data.categories && Object.entries(data.categories).slice(0, 2).map(([cat, info]) => (
              <div key={cat} className="bg-white rounded-xl border border-slate-200 p-6">
                <p className="text-xs text-slate-500 uppercase tracking-wide font-semibold">{cat}</p>
                <p className="text-3xl font-bold text-slate-900 mt-2">{info.count}</p>
                {info.total_value > 0 && <p className="text-sm text-slate-500 mt-1">${info.total_value.toLocaleString()}</p>}
              </div>
            ))}
          </div>

          {/* Category Breakdown */}
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="text-sm font-semibold text-slate-900 mb-4">Leak Categories</h3>
            <div className="space-y-3">
              {data.categories && Object.entries(data.categories).map(([cat, info]) => {
                const meta = CATEGORY_META[cat] || { icon: AlertTriangle, color: 'text-slate-500', bg: 'bg-slate-100' };
                return (
                  <div key={cat} className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg">
                    <div className={`w-10 h-10 ${meta.bg} rounded-lg flex items-center justify-center shrink-0`}>
                      <meta.icon size={18} className={meta.color} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-slate-900">{cat}</p>
                      <p className="text-xs text-slate-500">{info.count} patients</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-bold text-slate-900">${(info.total_value || 0).toLocaleString()}</p>
                      <p className="text-xs text-slate-500">potential value</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="flex gap-3">
            <Link to="/leads" className="flex-1 text-center bg-slate-900 text-white py-3 rounded-lg text-sm font-medium hover:bg-slate-800 transition-all">
              View Leads in Pipeline
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
